/**
 * @module  public/js/utils/taskUtils.js
 * @summary Date helpers for CRM and mirror-task objects.
 *
 * @description
 * Centralises logic for interpreting the variety of timestamp fields that
 * come back from HubSpot / Salesforce or our own mirrorTasks collection.
 * Firestore `dueAt` is canonical when present; date-only CRM fields are
 * fallback projections for records that do not carry `dueAt`.
 *
 * ## Exports
 * – getTaskDueMillis(task)      – returns `Number | null`
 * – getDueDateDisplay(task)     – returns `YYYY-MM-DD | '(none)'`
 *
 * @version 1.0.0
 * @since   2025-05-02
 */

/**
 * Return the task’s due-date as epoch ms or `null` if undeterminable.
 * Accepts Salesforce/HubSpot fields and Firestore `dueAt`/`dueDate` values.
 *
 * @param {object} task
 * @returns {number|null}
 */
export function getTaskDueMillis(task) {
  if (task.dueAt instanceof Date) {
    return task.dueAt.getTime();
  }
  if (typeof task.dueAt === 'number') {
    return task.dueAt;
  }
  if (
    task.dueAt &&
    typeof task.dueAt === 'object' &&
    typeof task.dueAt._seconds === 'number'
  ) {
    const millis = task.dueAt._seconds * 1000;
    const extra = Math.floor((task.dueAt._nanoseconds || 0) / 1e6);
    return millis + extra;
  }
  if (task.dueAt?.toDate) {
    return task.dueAt.toDate().getTime();
  }
  if (task.ActivityDate) {
    // Salesforce string: "2025-05-02"
    return Date.parse(`${task.ActivityDate}T00:00:00`);
  }
  const dueDateString =
    typeof task.dueDate === 'string'
      ? task.dueDate
      : typeof task.metadata?.dueDate === 'string'
        ? task.metadata.dueDate
        : null;
  if (dueDateString) {
    const trimmed = dueDateString.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return Date.parse(`${trimmed}T00:00:00`);
    }
    const isoFirst = Date.parse(trimmed);
    if (!Number.isNaN(isoFirst)) return isoFirst;
    const fallback = Date.parse(`${trimmed}T00:00:00`);
    if (!Number.isNaN(fallback)) return fallback;
  }
  const ts =
    task.properties?.hs_timestamp || task.properties?.hs_task_due_date || null;
  return ts && !Number.isNaN(Date.parse(ts)) ? Date.parse(ts) : null;
}

/**
 * Human-readable YYYY-MM-DD string (or “(none)”).
 *
 * @param {object} task
 * @returns {string}
 */
export function getDueDateDisplay(task) {
  const ms = getTaskDueMillis(task);
  return ms ? new Date(ms).toISOString().slice(0, 10) : '(none)';
}
